import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceStore } from '@/modules/workspaces/workspace-store';
import { useOperatingStore } from '@/modules/operating/operating-store';

import {
  AUDIT_BATCH_WINDOW_MS as BATCH_WINDOW_MS,
  mergeChanges,
  uniqueDescriptors as uniq,
} from '@/modules/audit/audit-rules';

import type {
  AuditAction,
  AuditChange,
  AuditChannel,
  AuditEntityType,
  TeamAuditEntityType,
  WorkspaceAuditEntityType,
} from '@/modules/audit/audit-types';

export type {
  AuditAction,
  AuditChange,
  AuditChannel,
  AuditEntityType,
  TeamAuditEntityType,
  WorkspaceAuditEntityType,
} from '@/modules/audit/audit-types';

export interface AuditLogRecord {
  id: string;
  workspace_id: string;
  actor_user_id: string | null;
  actor_member_id: string | null;
  actor_name: string | null;
  entity_type: AuditEntityType;
  entity_id: string;
  entity_label: string | null;
  action: AuditAction;
  descriptors: string[];
  changes: AuditChange[];
  channel: AuditChannel | null;
  app_client_id: string | null;
  app_name: string | null;
  created_at: string;
  updated_at: string;
}

interface LogAuditBase {
  entityId: string;
  entityLabel?: string | null;
  action: AuditAction;
  descriptors?: string[];
  changes?: AuditChange[];
  /**
   * `false` writes an edit as its own entry. A discrete event folded into an
   * earlier edit can cancel against it and leave that entry describing the
   * opposite of what happened.
   */
  batch?: boolean;
  /** Override the actor (used when action is performed against another user, e.g. removing a member). */
  actorUserIdOverride?: string;
  workspaceIdOverride?: string;
}

/**
 * A delete takes the team from the row as it was before the delete; anything
 * else from the row as it is now. The database refuses a team-scoped entry
 * without one.
 */
export type LogAuditArgs = LogAuditBase & (
  | { entityType: TeamAuditEntityType; teamId: string }
  | { entityType: WorkspaceAuditEntityType; teamId?: never }
);

/**
 * The home team of the row a write returned. The write must select `team_id`;
 * a delete returns the row as it was before the delete.
 */
export function writtenTeamId(rows: unknown[]): string {
  return (rows[0] as { team_id: string }).team_id;
}

/**
 * Where a write is being recorded, when that is not the active session acting in
 * the active workspace.
 *
 * The Dashboard (#306) can act on a workspace belonging to a different
 * signed-in identity, whose token is a different Supabase client entirely
 * (`getSessionClient` in src/modules/auth/session-store.ts). Without this, the audit
 * row would name whoever happens to be active, against a workspace they may not
 * even belong to.
 */
export interface AuditContext {
  /** The acting identity's own client, so RLS applies as that identity. */
  client: SupabaseClient<any, any, any>;
  workspaceId: string;
  /** `session-store` already holds this per session, so it is never guessed. */
  userId: string;
  /** Falls into the display name when the workspace has no person for them. */
  email?: string;
}

/**
 * Resolve the actor from a context rather than from the active stores.
 *
 * Costs a round trip, where the active-session path reads state already in
 * memory. That is the price of naming the right person: another identity's
 * workspace membership and the people directory are not in this tab's stores.
 */
async function resolveActorIn(ctx: AuditContext) {
  const { data } = await ctx.client
    .from('workspace_members')
    .select('id, person_id, people(name)')
    .eq('workspace_id', ctx.workspaceId)
    .eq('user_id', ctx.userId)
    .maybeSingle();
  // Not a member of that workspace. RLS would refuse the audit row anyway, and
  // an audit entry attributing a write to a non-member would be a lie.
  if (!data) return null;
  const personName = (data as any).people?.name as string | undefined;
  return {
    userId: ctx.userId,
    memberId: (data as any).id ?? null,
    // No user_metadata here: reading it would mean an auth call as that
    // identity, and the person name is the one the workspace actually uses.
    name: personName || ctx.email?.split('@')[0] || 'Unknown',
  };
}

export async function resolveActor(ctx?: AuditContext) {
  if (ctx) return resolveActorIn(ctx);
  const { data } = await supabase.auth.getUser();
  const user = data.user;
  if (!user) return null;
  const ws = useWorkspaceStore.getState();
  const me = ws.members.find((m) => m.userId === user.id);
  const person = me?.personId
    ? useOperatingStore.getState().people.find((tm) => tm.id === me.personId)
    : undefined;
  const name =
    person?.name ||
    (user.user_metadata as any)?.full_name ||
    (user.user_metadata as any)?.name ||
    me?.email?.split('@')[0] ||
    user.email ||
    'Unknown';
  return {
    userId: user.id,
    memberId: me?.id ?? null,
    name,
  };
}

/**
 * Append (or merge into recent) an audit log entry. Fire-and-forget; never throws.
 */
export async function logAudit(args: LogAuditArgs, ctx?: AuditContext): Promise<void> {
  try {
    // Every read and write below goes through this client, so the batch-window
    // lookup and the row itself are both subject to the acting identity's RLS.
    const db = ctx?.client ?? supabase;
    const wsId = ctx?.workspaceId ?? args.workspaceIdOverride ?? useWorkspaceStore.getState().currentWorkspaceId;
    if (!wsId) return;
    const actor = await resolveActor(ctx);
    if (!actor) return;
    const actorUserId = args.actorUserIdOverride ?? actor.userId;

    // Skip empty edits
    const descriptors = uniq(args.descriptors && args.descriptors.length ? args.descriptors : ['edited']);
    const changes = args.changes ?? [];
    if (args.action === 'edited' && changes.length === 0) return;

    if (args.action === 'edited' && args.batch !== false) {
      // Look for a recent matching log row to batch into — either a prior 'edited'
      // entry, or a 'created' entry from the same actor within the window.
      const cutoff = new Date(Date.now() - BATCH_WINDOW_MS).toISOString();
      let lookup = db
        .from('audit_logs')
        .select('*')
        .eq('workspace_id', wsId)
        .eq('actor_user_id', actorUserId)
        .eq('entity_type', args.entityType)
        .eq('entity_id', args.entityId);
      // An entity moved since that entry: its edits are filed under the new home.
      if (args.teamId) lookup = lookup.eq('team_id', args.teamId);
      const { data: recent } = await lookup
        .in('action', ['edited', 'created'])
        .gte('updated_at', cutoff)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (recent) {
        const mergedChanges = mergeChanges(
          (recent.changes as unknown as AuditChange[]) ?? [],
          changes,
        );
        if (mergedChanges.length === 0) {
          // All edits cancelled out; delete is not allowed by RLS, just leave the row alone.
          return;
        }
        // If folding into a 'created' row, suppress edit-style descriptors entirely
        // (the row stays a "created" entry with an expandable fields list).
        const mergedDescriptors =
          recent.action === 'created'
            ? (recent.descriptors ?? [])
            : uniq([...(recent.descriptors ?? []), ...descriptors]);
        const { data: merged } = await db
          .from('audit_logs')
          .update({
            descriptors: mergedDescriptors,
            changes: mergedChanges as any,
            entity_label: args.entityLabel ?? recent.entity_label,
          } as any)
          .eq('id', recent.id)
          .select('id');
        // The database closes the window on its own clock; past it, this edit
        // is recorded as an entry of its own.
        if (merged?.length) return;
      }
    }

    const id = crypto.randomUUID();
    const { error } = await db.from('audit_logs').insert({
      id,
      workspace_id: wsId,
      actor_user_id: actorUserId,
      actor_member_id: actor.memberId,
      actor_name: actor.name,
      entity_type: args.entityType,
      entity_id: args.entityId,
      entity_label: args.entityLabel ?? null,
      action: args.action,
      descriptors,
      changes: changes as any,
      ...(args.teamId ? { team_id: args.teamId } : {}),
    } as any);
    if (error) console.warn('[audit-log] refused', error.message);
  } catch (err) {
    console.warn('[audit-log] failed', err);
  }
}

export { descriptorsFor, diffFields } from '@/modules/audit/audit-rules';
